import { useState, useRef, useEffect } from "react";
import Calendar from "./Calendar";
import styled from "styled-components";
import dateFormats from '../../data/dateFormats.json'

import { Form } from "../form/FormComponents";
import useOutsideClick from "../../hooks/useOutsideClick";
import moment from "moment/moment";

import * as AiIcon from 'react-icons/ai'

//Getting local variables from the profile
import useProfile from '../../hooks/useProfile'
import { snapshot } from "valtio";

const InputField = styled(Form.Control)`

`
const DateIcon = styled.div`
  position: absolute;
  top: 2px;
  right: 10px;
  font-size: 20px;
  cursor: pointer;
`


const DateWrapper = styled.div`
  position: relative;

  p {
    font-size: 1rem;
  }
`


const DateInput = ({ name, id, value, setValue, align, testId, min, max }) => {
  const { pState } = useProfile()
  const profile = snapshot(pState)
  const locale = dateFormats[profile.currentOrgCountry] ?? 'DD/MM/YYYY'

  const [displayDate, setDisplayDate] = useState(() => convertToString(new Date(), locale));
  const [date, setDate] = useState(new Date())
  const [showCalendar, setShowCalendar] = useState(false);

  // Always close on outside click — never toggle open
  const handleClickOutside = () => {
    setShowCalendar(false);
  };

  const menuRef = useRef(null)
  useOutsideClick(menuRef, handleClickOutside, 'date');

  // On mount: if the parent has no value yet, commit today's date so the
  // displayed default is also reflected in state (prevents "empty required field").
  useEffect(() => {
    if (!value) {
      setValue({ target: { name: name, value: convertToIso(moment()) } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally runs once on mount only

  // Sync when the controlled value or locale changes
  useEffect(
    () => {
      if (value) {
        const mDate = moment(value)
        if (mDate.isValid()) {
          setDisplayDate(convertToString(mDate.toDate(), locale))
          setDate(mDate.toDate())
        }
      }
    }, [value, locale]
  )


  const handleInputChange = (e) => {
    const stringDate = e.target.value
    setDisplayDate(stringDate)
    const mDate = moment(stringDate, locale, true)
    if (mDate.isValid()) {
      setValue({ target: { name: name, value: convertToIso(mDate) } })
      setDate(mDate.toDate())
    }
  };

  const handleCalendarToggle = (e) => {
    setShowCalendar(!showCalendar);
    const mDate = moment(displayDate, locale, true)
    if (mDate.isValid()) {
      setDate(mDate.toDate())
    }
  };

  const handleDateSelect = (d) => {
    setValue({ target: { name: name, value: convertToIso(d) } })
    setDisplayDate(convertToString(d, locale))
    setShowCalendar(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      handleCalendarToggle(e);
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setShowCalendar(false);
    }
  };

  return (
    <DateWrapper id="dateWrapper">
      <InputField
        name={name}
        id={id || name}
        type="text"
        value={displayDate}
        onChange={handleInputChange}
        onClick={handleCalendarToggle}
        autoComplete="off"
        onKeyDown={handleKeyDown}
        data-testid={testId}
      />
      <DateIcon onClick={(e) => { e.stopPropagation(); handleCalendarToggle(e); }}><AiIcon.AiOutlineCalendar /></DateIcon>
      <Calendar
        isVisible={showCalendar}
        ref={menuRef}
        date={date}
        setDate={setDate}
        onSelect={handleDateSelect}
        $align={align}
        min={min}
        max={max}
      />
    </DateWrapper>
  );
};


//Converts the date to ISO at 12:00 Local Time
function convertToIso(localDate) {
  if (!moment.isMoment(localDate)) {
    localDate = moment(localDate)
  }
  const newDate = localDate.hour(12)
  return newDate.toISOString()
}

function convertToString(localDate, locale) {
  if (!moment.isMoment(localDate)) {
    localDate = moment(localDate)
  }
  const newDate = localDate.format(locale)
  return newDate
}

export default DateInput;
